import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, input, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  ActivationFormDialogComponent,
  DeliveryMode,
  DisplayActivationDialogComponent,
  ManualActivation,
} from '@lib/activation';
import { ROUTE } from '@lib/core/const';
import { PERMISSION } from '@lib/core/enum';
import { SmtpStatus } from '@lib/core/model';
import { PermissionService } from '@lib/core/permission';
import { SmtpStatusService } from '@lib/core/service';
import { TokenService } from '@lib/core/token';
import { Permission } from '@lib/core/type';
import { DialogService } from '@proget-shared/dialog';
import { TranslatedDialogService } from '@proget-shared/dialog/translated-dialog';
import { GridAction } from '@proget-shared/grid/grid-actions';
import { ResponseErrorTemplateMapper } from '@proget-shared/helper/response-error';
import { TranslateModule } from '@proget-shared/translate';
import { OverlayPanelDirective, OverlayPanelModule } from '@proget-shared/ui/overlay-panel';
import { ToastService } from '@proget-shared/ui/toast';
import {
  catchError, combineLatest, EMPTY, filter,
  noop, of, switchMap, take, throwError,
} from 'rxjs';

import { User } from '../../model';
import { UserCardService } from '../../service/user-card.service';
import { ChangePasswordComponent } from '../../shared/change-password/change-password.component';
import { GeneratedPinComponent } from '../../shared/generated-pin/generated-pin.component';
import { ResetPasswordComponent } from '../../shared/reset-password/reset-password.component';
import { SetPasswordUrlService } from '../../shared/set-password-url';
import {
  UserCertificatesFormDialogComponent,
} from '../../shared/user-certificates-form-dialog/component/user-certificates-form-dialog.component';
import { UserCardRepository } from '../../user-card.repository';
import { MailFormDialogComponent } from '../mail-form-dialog/mail-form-dialog.component';
import { ManageLabelsComponent } from '../manage-labels/manage-labels.component';

type Action = {
  label: string;
  icon: string;
  permission: Permission;
  action: () => void;
  enabled?: boolean;
};

@Component({
  selector: 'lib-user-card-actions',
  templateUrl: './user-card-actions.component.html',
  styleUrls: ['./user-card-actions.component.scss'],
  imports: [
    CommonModule,
    OverlayPanelModule,
    TranslateModule,
  ],
})
export class UserCardActionsComponent {
  public readonly user = input<User>();

  protected actions: Action[];

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly tokenService = inject(TokenService);
  private readonly smtpStatusService = inject(SmtpStatusService);
  private readonly setPasswordUrl = inject(SetPasswordUrlService);
  private readonly dialogService = inject(DialogService);
  private readonly translatedDialogService = inject(TranslatedDialogService);
  private readonly toastService = inject(ToastService);
  private readonly userCardRepository = inject(UserCardRepository);
  private readonly userCardService = inject(UserCardService);
  private readonly overlayPanel = viewChild('actionsPanel', { read: OverlayPanelDirective });

  constructor() {
    combineLatest([
      toObservable(this.user)
        .pipe(filter((user): user is User => !!user)),
      this.tokenService.token$
        .pipe(
          filter((token) => null !== token),
          take(1),
          switchMap(() => this.userCardRepository.getSelf())
        ),
      this.smtpStatusService.get(),
      this.permissionService.permissions$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([user, loggedUser, smtpStatus]) => {
          this.actions = this.prepareActions(user, loggedUser, smtpStatus);
        },
      });
  }

  public performAction(item: Action): void {
    const overlayPanel = this.overlayPanel();

    if (!overlayPanel) {
      return;
    }

    overlayPanel.close(true);
    item.action();
  }

  protected getQaAttribute(action: GridAction): string {
    return `user-action-${action.label.replace(/\./g, '-')}`;
  }

  private prepareActions(user: User, loggedUser: User, smtpStatus: SmtpStatus): Action[] {
    return [
      {
        label: 'library.user_card.view.button.generate_activation.label',
        icon: 'icon-send-activation-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: !user.locked,
        action: () => this.generateActivation(user),
      },
      {
        label: 'library.user_card.view.button.display_activation.label',
        icon: 'icon-send-activation-icon',
        permission: PERMISSION.USER_CARD_ALLOW_VIEW,
        enabled: !user.locked && user.hasActiveActivation,
        action: () => this.displayActivation(user),
      },
      {
        label: 'library.user_card.action.delete_activation.header',
        icon: 'icon-delete-activation-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: user.hasActiveActivation,
        action: () => this.deleteActivation(user),
      },
      {
        label: 'library.user_card.action.send_email.header',
        icon: 'icon-send-msg-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: smtpStatus.active,
        action: () => this.sendEmail(user),
      },
      {
        label: 'library.user_card.action.mark_as_vip.header',
        icon: 'icon-mark-vip-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: !user.vip,
        action: () => this.markAsVip(user),
      },
      {
        label: 'library.user_card.action.unmark_as_vip.header',
        icon: 'icon-unmark-vip-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: user.vip,
        action: () => this.unmarkAsVip(user),
      },
      {
        label: 'label.action.manage_labels.header',
        icon: 'icon-label-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        action: () => this.manageLabels(user),
      },
      {
        label: 'library.user_card.action.reset_two_factor.header',
        icon: 'icon-restart-authentication-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        action: () => this.resetTwoFactor(user),
      },
      {
        label: 'library.user_card.action.reset_password.header',
        icon: 'icon-refresh-pass-icon',
        permission: PERMISSION.ALLOW_RESET_USER_PASSWORD,
        enabled: user.canChangePassword && loggedUser?.uuid !== user.uuid,
        action: () => this.resetPassword(user, smtpStatus),
      },
      {
        label: 'library.user_card.action.change_password.header',
        icon: 'icon-change-pass-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: user.canChangePassword && loggedUser?.uuid === user.uuid,
        action: () => this.changePassword(user),
      },
      {
        label: 'library.user_card.action.lock.header',
        icon: 'icon-block-user-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: !user.locked && loggedUser?.uuid !== user.uuid,
        action: () => this.lock(user),
      },
      {
        label: 'library.user_card.action.unlock.header',
        icon: 'icon-unblock-user-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: user.locked,
        action: () => this.unlock(user),
      },
      {
        label: 'library.user_card.action.assign_super_admin.header',
        icon: 'icon-add-superadmin-icon',
        permission: PERMISSION.SUPER_ADMIN,
        enabled: !user.superAdmin && loggedUser.superAdmin,
        action: () => this.assignSuperAdmin(user),
      },
      {
        label: 'library.user_card.action.unassign_super_admin.header',
        icon: 'icon-remove-superadmin-icon',
        permission: PERMISSION.SUPER_ADMIN,
        enabled: user.superAdmin && loggedUser.superAdmin && loggedUser.getId() !== user.getId(),
        action: () => this.unassignSuperAdmin(user),
      },
      {
        label: 'library.user_card.action.generate_pin.header',
        icon: 'icon-pin-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        action: () => this.generatePin(user),
      },
      {
        label: 'library.user_card.action.delete_user.header',
        icon: 'icon-delete-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: loggedUser?.uuid !== user.uuid,
        action: () => this.deleteUser(user),
      },
      {
        label: 'library.user_card.dialog.user_certificates_form.header',
        icon: 'icon-generate-certificate-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        action: () => this.manageCertificates(user),
      },
    ]
      .filter((action) => this.permissionService.hasAnyPermissionInstant(action.permission))
      .filter((action) => undefined === action.enabled || action.enabled);
  }

  private generateActivation(user: User): void {
    this.dialogService
      .custom(ActivationFormDialogComponent)
      .pipe(
        catchError(() => EMPTY),
        switchMap((form) => this.userCardRepository.createActivation(
          user.uuid,
          form.activation.id,
          form.activation.deliveryMode === DeliveryMode.ASK ? form.sendEmail : form.activation.deliveryMode === DeliveryMode.SEND
        ))
      )
      .subscribe({
        next: (activation) => {
          this.userCardService.reloadUser();
          this.displayActivationDialog(activation);
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.generate_activation.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private displayActivation(user: User): void {
    this.userCardRepository
      .getActivation(user.uuid)
      .pipe(
        switchMap((activation) => (activation.active ? of(activation) : throwError(() => 'Activation inactive')))
      )
      .subscribe({
        next: (activation) => {
          this.displayActivationDialog(activation);
        },
        error: () => {
          this.toastService.error('library.user_card.toast.display_activation.error');
        },
      });
  }

  private displayActivationDialog(activation: ManualActivation): void {
    this.dialogService
      .custom(DisplayActivationDialogComponent, {
        configuration: {
          activation,
        },
      })
      .subscribe({
        next: noop,
        error: () => {
          this.toastService.error('library.user_card.toast.display_activation.error');
        },
      });
  }

  private deleteActivation(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.delete_activation.header',
        {
          key: 'library.user_card.action.delete_activation.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-delete-activation-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.deleteActivation(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.delete_activation.success');
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.delete_activation.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private sendEmail(user: User): void {
    this.dialogService
      .custom(
        MailFormDialogComponent,
        {
          configuration: {
            user,
          },
        }
      )
      .pipe(
        catchError(() => EMPTY)
      )
      .subscribe({
        next: () => {
          this.toastService.success('library.user_card.toast.send_email.success');
        },
      });
  }

  private markAsVip(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.mark_as_vip.header',
        {
          key: 'library.user_card.action.mark_as_vip.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-mark-vip-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.markAsVip(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.mark_as_vip.success');
        },
        error: () => {
          this.toastService.error('library.user_card.toast.mark_as_vip.error');
        },
      });
  }

  private unmarkAsVip(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.unmark_as_vip.header',
        {
          key: 'library.user_card.action.unmark_as_vip.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-unmark-vip-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.unmarkVip(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.unmark_as_vip.success');
        },
        error: () => {
          this.toastService.error('library.user_card.toast.unmark_as_vip.error');
        },
      });
  }

  private manageLabels(user: User): void {
    this.dialogService
      .custom(
        ManageLabelsComponent,
        { configuration: { user } }
      )
      .pipe(
        catchError(() => EMPTY)
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
        },
      });
  }

  private resetTwoFactor(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.reset_two_factor.header',
        {
          key: 'library.user_card.action.reset_two_factor.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-restart-authentication-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.resetTwoFactor(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.reset_two_factor.success');
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.reset_two_factor.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private resetPassword(user: User, smtpStatus: SmtpStatus): void {
    this.dialogService
      .custom(ResetPasswordComponent, { configuration: { smtpStatus } })
      .pipe(
        catchError(() => EMPTY),
        switchMap((language) => this.userCardRepository.resetPassword(
          user.uuid,
          language
        ))
      )
      .subscribe({
        next: ({ url }) => {
          if (url) {
            this.setPasswordUrl.openDialog(url);
          }
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.reset_password.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private changePassword(user: User): void {
    this.dialogService
      .custom(ChangePasswordComponent, { configuration: { userId: user.uuid } });
  }

  private lock(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.lock.header',
        {
          key: 'library.user_card.action.lock.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-block-user-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.lock(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.lock.success');
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.lock.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private unlock(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.unlock.header',
        {
          key: 'library.user_card.action.unlock.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-unblock-user-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.unlock(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.unlock.success');
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.unlock.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private assignSuperAdmin(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.assign_super_admin.header',
        {
          key: 'library.user_card.action.assign_super_admin.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-add-superadmin-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.assignSuperAdmin(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.assign_super_admin.success');
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.assign_super_admin.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private unassignSuperAdmin(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.unassign_super_admin.header',
        {
          key: 'library.user_card.action.unassign_super_admin.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-remove-superadmin-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.unassignSuperAdmin(user.uuid) : EMPTY))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
          this.toastService.success('library.user_card.toast.unassign_super_admin.success');
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.unassign_super_admin.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private generatePin(user: User): void {
    this.translatedDialogService
      .confirm(
        'library.user_card.action.generate_pin.header',
        {
          key: 'library.user_card.action.generate_pin.message',
          params: { fullName: user.getDisplayName() },
        },
        { configuration: { iconClass: 'icon-pin-icon' } }
      )
      .pipe(
        switchMap((value) => (value ? this.userCardRepository.generatePin(user.uuid) : EMPTY)),
        switchMap((pin) => this.dialogService.custom(
          GeneratedPinComponent,
          { configuration: { items: [{ pin, username: user.username }] } }
        ).pipe(catchError(() => of(null))))
      )
      .subscribe({
        next: () => {
          this.userCardService.reloadUser();
        },
        error: () => {
          this.toastService.error('library.user_card.toast.generate_pin.error');
        },
      });
  }

  private deleteUser(user: User): void {
    this.translatedDialogService
      .phraseConfirm(
        'library.user_card.action.delete_user.header',
        '',
        user.getDisplayName(),
        { configuration: { iconClass: 'icon-delete-icon' } }
      )
      .pipe(
        catchError(() => EMPTY),
        switchMap(() => this.userCardRepository.delete(user.uuid))
      )
      .subscribe({
        next: () => {
          this.router.navigate([ROUTE.BASE, ROUTE.USER.BASE]);
          this.toastService.success('library.user_card.toast.delete_user.success');
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.toast.delete_user.error',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }

  private manageCertificates(user: User): void {
    this.dialogService
      .custom(
        UserCertificatesFormDialogComponent,
        { configuration: { userId: user.uuid } }
      );
  }
}
